import { ApiFile, getApiFileName, getBaseUrl } from '@/shared';
import { Chatmessage, ChatmessageRoleUserMsg, ChatmessageThread } from 'simpleloan_api';

const IMAGE_EXTENSIONS = ['png', 'jpg', 'jpeg', 'svg', 'webp', 'gif'];

/** Файл сообщения: бэк кладёт его в chat_file.file, в типе пакета он описан как file */
function getMessageApiFile(message: Chatmessage) {
  const withChatFile = message as Chatmessage & { chat_file?: { file?: ApiFile | null } };
  return withChatFile.chat_file?.file ?? message.file ?? null;
}

export function getMessageFileUrl(message: Chatmessage) {
  const file = getMessageApiFile(message);
  return file?.file ? getBaseUrl() + file.file : null;
}

export function getMessageFileName(message: Chatmessage) {
  const file = getMessageApiFile(message);
  return file ? getApiFileName(file) : null;
}

export function isMessageFileImage(message: Chatmessage) {
  const path = getMessageApiFile(message)?.file?.toLowerCase();
  return Boolean(path && IMAGE_EXTENSIONS.some(ext => path.includes(ext)));
}

/** Роль текущего пользователя в чате */
export function getMyChatRole(isChatSupport: boolean): ChatmessageRoleUserMsg {
  return isChatSupport ? 'admin' : 'user';
}

/** Сообщение или тред от собеседника, а не от своей стороны */
export function isIncoming(item: Chatmessage | ChatmessageThread, isChatSupport: boolean) {
  return item.role_user_msg !== getMyChatRole(isChatSupport);
}

/** Время сообщения ЧЧ:ММ, как в старом чате */
export function formatMessageTime(dateString: string) {
  const date = new Date(dateString);
  return `${String(date.getUTCHours()).padStart(2, '0')}:${String(date.getUTCMinutes()).padStart(2, '0')}`;
}

function isSameDay(a: Date, b: Date) {
  return a.toDateString() === b.toDateString();
}

function isYesterday(date: Date) {
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  return isSameDay(date, yesterday);
}

/** Подпись дня над сообщениями: «Сегодня», «Вчера» или дата */
export function formatDayLabel(dateString: string) {
  const date = new Date(dateString);
  if (isSameDay(date, new Date())) return 'Сегодня';
  if (isYesterday(date)) return 'Вчера';
  return date.toLocaleDateString();
}

/** Время в списке тредов: сегодня — ЧЧ:ММ, вчера — «Вчера», иначе ДД.ММ */
export function formatThreadTime(dateString: string) {
  const date = new Date(dateString);
  if (isSameDay(date, new Date())) return formatMessageTime(dateString);
  if (isYesterday(date)) return 'Вчера';
  return date.toLocaleDateString(undefined, { day: '2-digit', month: '2-digit' });
}

/** Инициалы для аватара: первые буквы двух слов или два первых символа */
export function getInitials(title: string) {
  const words = title.trim().split(/\s+/).filter(Boolean);
  if (words.length >= 2) {
    return (words[0][0] + words[1][0]).toUpperCase();
  }
  return (words[0] ?? '').slice(0, 2).toUpperCase();
}

/** Раскладка переписки: пузыри по сторонам или список друг под другом */
export type ChatmessageLayout = 'bubbles' | 'list';
